import { apiClient } from '../api/fetchClient';
import { Cart } from './models/cart.model';
import type { CartAttributes } from './models/cart-attributes.model';
import type { Checkout, CheckoutWrapper } from './models/checkout.model';
import type { LineItem } from './models/line-item.model';
import type { LineItemList } from './models/line-item-list.model';
import type { Product, ProductWrapper } from './models/product.model';
import type { VariantAttribute } from './models/variant-attribute.model';

class ShopifyClient {
  /**
   * Fetches the current state of the cart.
   */
  async getCart(): Promise<Cart> {
    return apiClient
      .get<Cart>('/cart.js', {
        _: new Date().getTime().toString(),
      })
      .then((response) => Object.assign(new Cart(), response));
  }

  /**
   * Changes the quantity in the cart.
   *
   * @param key the line item key
   *
   * @return the updated cart.
   */
  async changeCart(key: string, quantity: number): Promise<Cart> {
    return apiClient
      .post<Cart>('/cart/change.js', {
        id: key,
        quantity,
      })
      .then((response) => Object.assign(new Cart(), response));
  }

  /**
   * Update cart information
   * @param attributes cart attributes
   */
  async updateCart(attributes: CartAttributes): Promise<Cart> {
    const response = await apiClient.post<Cart>('/cart/update.js', {
      attributes,
    });
    return Object.assign(new Cart(), response);
  }

  async updateCartProperties(id: number, properties: any): Promise<string> {
    return await apiClient.post<string>(
      '/cart/change',
      {
        id: id.toString(),
        properties,
      },
      {
        observe: 'response' as const,
        responseType: 'text' as const,
      }
    );
  }

  /**
   * Load product data by its handle.
   *
   * @param handle the handle
   * @param includeAttributes whether or not to look up product attributes.
   */
  async getProduct(handle: string, includeAttributes?: boolean): Promise<Product> {
    return await apiClient.get<Product>(`/products/${handle}.js`).then((product) => {
      if (!includeAttributes) return product;

      return Promise.all(
        product.variants.map(async (variant) => {
          const attributes = await this.lookupProductAttributes(variant.id, handle);
          variant.attributes = (attributes ?? []).filter((a) => a?.value !== '');
          return variant;
        })
      ).then(() => product);
    });
  }

  /**
   * Load product data by its handle in order to retrieve converted pricing data
   *
   * @param handle the handle
   */
  async getProductPriceData(handle: string): Promise<Product> {
    return await apiClient
      .get<ProductWrapper>(`/products/${handle}.json`)
      .then((response) => response.product);
  }

  async addItemsToCart(
    items: LineItem[],
    sections?: string[],
    sectionsUrl?: string
  ): Promise<LineItemList & { sections?: Record<string, string> }> {
    const body: any = { items };

    if (sections && sections.length > 0) {
      body.sections = sections;
    }

    if (sectionsUrl) {
      body.sections_url = sectionsUrl;
    }

    return await apiClient.post<LineItemList & { sections?: Record<string, string> }>(
        '/cart/add.js',
        body
    );
  }

  async buyItNow(items: LineItem[], accessToken: string): Promise<Checkout> {
    return await apiClient
      .post<CheckoutWrapper>(
        '/wallets/checkouts.json',
        {
          checkout: {
            wallet_name: 'Checkout',
            line_items: items,
          },
        },
        {},
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Basic ${btoa(accessToken)}`,
          },
        }
      )
      .then((response) => response.checkout)
      .catch((err) => {
        const erroredLineItems = err?.error?.errors?.line_items;
        if (!!erroredLineItems) {
          const filteredLineItems = items.filter((value, index) => !(index in erroredLineItems));
          if (filteredLineItems.length > 0) {
            return this.buyItNow(filteredLineItems, accessToken);
          }
        }
        throw err;
      });
  }

  async lookupProductAttributes(
    variantId: number,
    productHandle: string
  ): Promise<VariantAttribute[]> {
    return await apiClient.get<VariantAttribute[]>(
      '/apps/single/v3/content/product/variant-attributes',
      {
        variantId,
        productHandle,
      }
    );
  }
}

// Export singleton instance
export const shopifyClient = new ShopifyClient();

// Export class type for cases where the type is needed
export type { ShopifyClient };
